import { t, Trans } from '@lingui/macro';
import { BookingDetailsForCustomer } from 'noddi-types';
import { NoddiBasicCard, NoddiChip, NoddiDummyButton } from 'noddi-ui-web';
import { customerRoutes, DateFormats, noddiFormatDate } from 'noddi-util';

import { TZDate } from '@date-fns/tz';
import { noddiColors, NoddiIcon } from 'noddi-ui-common';

const CompletedBookingChip = ({ booking }: { booking: BookingDetailsForCustomer }) => {
  if (booking.isFullyUnableToComplete) {
    return <NoddiChip size='small' variant='destructive' label={t`Not completed`} />;
  }
  if (booking.isFullyPaid) {
    return <NoddiChip size='small' variant='success' label={t`Paid`} />;
  }
  if (booking.isManuallyInvoiced) {
    return <NoddiChip size='small' variant='warning' label={t`Invoiced`} />;
  }
  return <NoddiChip size='small' variant='destructive' label={t`Unpaid`} />;
};

type BookingCardProps = {
  booking: BookingDetailsForCustomer;
  isUpcomingBooking?: boolean;
};

export const BookingCard = ({ booking, isUpcomingBooking }: BookingCardProps) => {
  const bookingTime = `${noddiFormatDate(new TZDate(booking.deliveryWindowStartsAt, booking.address.timeZone), DateFormats.TIME)} - ${noddiFormatDate(new TZDate(booking.deliveryWindowEndsAt, booking.address.timeZone), DateFormats.TIME)}`;
  const bookingDate = `${noddiFormatDate(
    new TZDate(booking.date, booking.address.timeZone),
    DateFormats.FULL_WEEKDAY_MONTH_AND_YEAR
  )}`;

  return (
    <NoddiBasicCard to={customerRoutes.myBookingDetails.getPath({ id: booking.id })} className='flex flex-col gap-4'>
      <div className='flex items-center justify-between gap-4'>
        <div className='flex flex-col'>
          <h6 className='text-xs'>
            <Trans>Booking reference</Trans>
          </h6>
          <h6 className='font-bold text-primary md:text-xl'>{booking.slug}</h6>
        </div>
        {!isUpcomingBooking && <CompletedBookingChip booking={booking} />}
      </div>

      <div className='flex max-h-48 flex-col gap-2 overflow-y-auto'>
        {booking.cars.map((car, bookingItemIdx) => (
          <div key={bookingItemIdx} className='flex flex-col gap-2'>
            <div>
              <p className='font-bold'>{`${car.make} ${car.model}`}</p>
              <p className='font-bold'>{car.licensePlate.number}</p>
            </div>

            <div className='flex flex-wrap gap-x-2'>
              {car.orderLines.map(({ description }, serviceIndex) => (
                <span key={car.licensePlate.number + serviceIndex}>
                  {description}
                  {serviceIndex < car.orderLines.length - 1 && ','}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
      {isUpcomingBooking && (
        <div className='flex flex-col gap-1'>
          <div className='flex items-center gap-2'>
            <div>
              <NoddiIcon color={noddiColors.black} name='ClockCircle' />
            </div>
            <p>{bookingTime}</p>
          </div>
          <div className='flex items-center gap-2'>
            <div>
              <NoddiIcon color={noddiColors.black} name='Calendar' />
            </div>
            <p className='capitalize'>{bookingDate}</p>
          </div>
          <div className='flex items-center gap-2'>
            <div>
              <NoddiIcon name='LocationPin' color={noddiColors.black} />
            </div>
            <p>
              {booking.address.streetName} {booking.address.streetNumber}
            </p>
          </div>
        </div>
      )}

      {!isUpcomingBooking && (
        <div className='flex items-center gap-2'>
          <div>
            <NoddiIcon color={noddiColors.black} name='Calendar' />
          </div>
          <p className='capitalize'>{bookingDate}</p>
        </div>
      )}

      <div className='flex items-end justify-end'>
        <NoddiDummyButton variant='link' className='-mb-2 -mr-4' endIcon='ArrowRight'>
          <Trans>See booking</Trans>
        </NoddiDummyButton>
      </div>
    </NoddiBasicCard>
  );
};
